import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { ApiFilter, ApiPage, ApiSort } from "@result/rui/utils/api";

import apiClient from "#/redux/api/ApiClient";
import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/dog/oznacevanje/oznDrob";

export const getSmallLivestockMarking = createAsyncThunk(
  "marking/get",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateSmallLivestockMarking = createAsyncThunk(
  "marking/update",
  async ({ dto, id }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`${baseUrl}/${id}`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createSmallLivestockMarking = createAsyncThunk(
  "marking/create",
  async ({ dto }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(baseUrl, dto);
      return response.data;
    } catch (error) {
      const errorText = error?.result?.[0]?.text;
      return rejectWithValue(errorText ?? getErrorMessage(error));
    }
  }
);

export const getSmallLivestockMarkingPage = createAsyncThunk(
  "marking/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        false
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSmallLivestockMarkingsForLambingId = createAsyncThunk(
  "marking/forLambingId",
  async (lambingId, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        new ApiPage().page(0).size(100).toQueryString(),
        new ApiFilter().where("kotitevId", "EQ", lambingId),
        new ApiSort().sort("datumOznacitve", "asc").toQueryString(),
        false
      );
      return {
        markings: response.data.content ?? [],
        lambingId: lambingId,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const thunks = [
  createSmallLivestockMarking,
  updateSmallLivestockMarking,
  getSmallLivestockMarking,
  getSmallLivestockMarkingPage,
  getSmallLivestockMarkingsForLambingId,
];

const markingSmallLivestockSlice = createSlice({
  name: "marking",
  initialState: {
    marking: null,
    page: {},
    error: null,
    isLoading: false,
    forLambingId: {},
    draft: null,
  },
  reducers: {
    setMarkingDraft(state, action) {
      state.draft = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSmallLivestockMarking.fulfilled, (state, action) => {
        state.marking = action.payload;
      })
      .addCase(updateSmallLivestockMarking.fulfilled, (state, action) => {
        state.marking = action.payload;
      })
      .addCase(getSmallLivestockMarking.fulfilled, (state, action) => {
        state.marking = action.payload;
      })
      .addCase(getSmallLivestockMarkingPage.fulfilled, (state, action) => {
        state.page = action.payload;
      })
      .addCase(
        getSmallLivestockMarkingsForLambingId.fulfilled,
        (state, action) => {
          state.forLambingId[action.payload.lambingId] =
            action.payload.markings;
        }
      )
      .addMatcher(
        isAnyOf(...thunks.map((t) => t.rejected)),
        (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        }
      )
      .addMatcher(isAnyOf(...thunks.map((t) => t.pending)), (state, _) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(isAnyOf(...thunks.map((t) => t.fulfilled)), (state, _) => {
        state.isLoading = false;
      });
  },
});

export const { setMarkingDraft } = markingSmallLivestockSlice.actions;

export default markingSmallLivestockSlice.reducer;
